import { v4 as uuid } from "uuid";

import {
  BB_ToolPaletteActionId,
  BB_ToolPaletteCutId,
  BB_ToolPaletteMarkerId,
  BB_ToolPaletteMiscId,
  BB_ToolPalettePassId,
  BB_ToolPalettePlayerId,
  BB_ToolPaletteScreenId,
  BB_ToolPaletteStencilId,
  isBBActionId,
  isBBCutId,
  isBBDHOId,
  isBBDribbleId,
  isBBMarkerId,
  isBBMiscId,
  isBBPassId,
  isBBPlayerId,
  isBBScreenId,
} from "../../config";
import { MarkerStencil } from "../../redux/slices";
import {
  ActionBase,
  ActionType,
  BallActionBase,
  BasketballActionLineType,
  BasketballBallActionLineType,
  BasketballMiscType,
  BasketballPlayerActionLineType,
  FrameStencil,
  MarkerBase,
  MarkerType,
  MidPoint,
  Player,
  Point,
  PointType,
  StencilType,
} from "../../types";

const getStencilType = (id: BB_ToolPaletteStencilId): StencilType => {
  if (isBBMarkerId(id)) return StencilType.MARKER;
  else if (isBBActionId(id)) return StencilType.ACTION;
  else throw new Error(`Invalid BB_ToolPaletteStencilId: ${id}`);
};

const getMarkerType = (id: BB_ToolPaletteMarkerId): MarkerType => {
  if (isBBPlayerId(id)) return MarkerType.PLAYER;
  else if (isBBMiscId(id)) return MarkerType.MISC;
  else if (id === "MISC_TEXTBOX") return MarkerType.TEXT;
  else if (id === "SHADING_RECTANGLE") return MarkerType.SHADING_RECTANGLE;
  else if (id === "SHADING_OVAL") return MarkerType.SHADING_OVAL;
  else if (id === "SHADING_TRAPEZOID") return MarkerType.SHADING_TRAPEZOID;
  else throw new Error(`Invalid BB_ToolPaletteMarkerId: ${id}`);
};

const getStencilName = (id: BB_ToolPaletteMarkerId): MarkerStencil["stencilName"] => {
  switch (id) {
    case "BB_PLAYER_1":
    case "BB_PLAYER_2":
    case "BB_PLAYER_3":
    case "BB_PLAYER_4":
    case "BB_PLAYER_5":
    case "BB_PLAYER_POSSESSION_1":
    case "BB_PLAYER_POSSESSION_2":
    case "BB_PLAYER_POSSESSION_3":
    case "BB_PLAYER_POSSESSION_4":
    case "BB_PLAYER_POSSESSION_5":
    case "BB_PLAYER_FILL_1":
    case "BB_PLAYER_FILL_2":
    case "BB_PLAYER_FILL_3":
    case "BB_PLAYER_FILL_4":
    case "BB_PLAYER_FILL_5":
    case "BB_PLAYER_DEF_1":
    case "BB_PLAYER_DEF_2":
    case "BB_PLAYER_DEF_3":
    case "BB_PLAYER_DEF_4":
    case "BB_PLAYER_DEF_5":
      return "BasketballPlayerStencil";

    case "MISC_TEXTBOX":
      return "FreeTextStencil";

    case "MISC_HOOP":
      return "HoopStencil";
    case "MISC_BALL":
      return "BallStencil";
    case "MISC_FLAG":
      return "FlagStencil";
    case "MISC_CONE":
      return "ConeStencil";

    case "SHADING_RECTANGLE":
      return "RectangleShadingStencil";
    case "SHADING_OVAL":
      return "OvalShadingStencil";
    case "SHADING_TRAPEZOID":
      return "TrapezoidShadingStencil";
  }
};

const playerHasPossession = (id: BB_ToolPalettePlayerId): boolean => {
  switch (id) {
    case "BB_PLAYER_POSSESSION_1":
    case "BB_PLAYER_POSSESSION_2":
    case "BB_PLAYER_POSSESSION_3":
    case "BB_PLAYER_POSSESSION_4":
    case "BB_PLAYER_POSSESSION_5":
      return true;

    case "BB_PLAYER_1":
    case "BB_PLAYER_2":
    case "BB_PLAYER_3":
    case "BB_PLAYER_4":
    case "BB_PLAYER_5":
    case "BB_PLAYER_FILL_1":
    case "BB_PLAYER_FILL_2":
    case "BB_PLAYER_FILL_3":
    case "BB_PLAYER_FILL_4":
    case "BB_PLAYER_FILL_5":
    case "BB_PLAYER_DEF_1":
    case "BB_PLAYER_DEF_2":
    case "BB_PLAYER_DEF_3":
    case "BB_PLAYER_DEF_4":
    case "BB_PLAYER_DEF_5":
      return false;
  }
};

const getPlayerIdNumber = (id: BB_ToolPalettePlayerId): Player["playerIdNumber"] => {
  switch (id) {
    case "BB_PLAYER_1":
    case "BB_PLAYER_POSSESSION_1":
    case "BB_PLAYER_FILL_1":
    case "BB_PLAYER_DEF_1":
      return 1;

    case "BB_PLAYER_2":
    case "BB_PLAYER_POSSESSION_2":
    case "BB_PLAYER_FILL_2":
    case "BB_PLAYER_DEF_2":
      return 2;

    case "BB_PLAYER_3":
    case "BB_PLAYER_POSSESSION_3":
    case "BB_PLAYER_FILL_3":
    case "BB_PLAYER_DEF_3":
      return 3;

    case "BB_PLAYER_4":
    case "BB_PLAYER_POSSESSION_4":
    case "BB_PLAYER_FILL_4":
    case "BB_PLAYER_DEF_4":
      return 4;

    case "BB_PLAYER_5":
    case "BB_PLAYER_POSSESSION_5":
    case "BB_PLAYER_FILL_5":
    case "BB_PLAYER_DEF_5":
      return 5;
  }
};

const getPlayerLabel = (id: BB_ToolPalettePlayerId): Player["label"] => {
  switch (id) {
    case "BB_PLAYER_1":
    case "BB_PLAYER_POSSESSION_1":
    case "BB_PLAYER_FILL_1":
      return "1";

    case "BB_PLAYER_2":
    case "BB_PLAYER_POSSESSION_2":
    case "BB_PLAYER_FILL_2":
      return "2";

    case "BB_PLAYER_3":
    case "BB_PLAYER_POSSESSION_3":
    case "BB_PLAYER_FILL_3":
      return "3";

    case "BB_PLAYER_4":
    case "BB_PLAYER_POSSESSION_4":
    case "BB_PLAYER_FILL_4":
      return "4";

    case "BB_PLAYER_5":
    case "BB_PLAYER_POSSESSION_5":
    case "BB_PLAYER_FILL_5":
      return "5";

    case "BB_PLAYER_DEF_1":
      return "x1";
    case "BB_PLAYER_DEF_2":
      return "x2";
    case "BB_PLAYER_DEF_3":
      return "x3";
    case "BB_PLAYER_DEF_4":
      return "x4";
    case "BB_PLAYER_DEF_5":
      return "x5";
  }
};

const getPlayerFill = (id: BB_ToolPalettePlayerId): Player["fill"] => {
  switch (id) {
    case "BB_PLAYER_FILL_1":
    case "BB_PLAYER_FILL_2":
    case "BB_PLAYER_FILL_3":
    case "BB_PLAYER_FILL_4":
    case "BB_PLAYER_FILL_5":
      return true;

    case "BB_PLAYER_1":
    case "BB_PLAYER_2":
    case "BB_PLAYER_3":
    case "BB_PLAYER_4":
    case "BB_PLAYER_5":
    case "BB_PLAYER_POSSESSION_1":
    case "BB_PLAYER_POSSESSION_2":
    case "BB_PLAYER_POSSESSION_3":
    case "BB_PLAYER_POSSESSION_4":
    case "BB_PLAYER_POSSESSION_5":
    case "BB_PLAYER_DEF_1":
    case "BB_PLAYER_DEF_2":
    case "BB_PLAYER_DEF_3":
    case "BB_PLAYER_DEF_4":
    case "BB_PLAYER_DEF_5":
      return false;
  }
};

const getMiscType = (id: BB_ToolPaletteMiscId): BasketballMiscType => {
  switch (id) {
    case "MISC_BALL":
      return BasketballMiscType.BALL_STATIC;
    case "MISC_HOOP":
      return BasketballMiscType.HOOP;
    case "MISC_CONE":
      return BasketballMiscType.CONE;
    case "MISC_FLAG":
      return BasketballMiscType.FLAG;
  }
};

const getActionType = (id: BB_ToolPaletteActionId): ActionType => {
  if (isBBCutId(id) || isBBScreenId(id)) return ActionType.PLAYER;
  else if (isBBPassId(id)) return ActionType.BALL;
  else if (isBBDHOId(id)) return ActionType.HANDOFF;
  else if (isBBDribbleId(id)) return ActionType.POSSESSION;
  else throw new Error(`Invalid BB_ToolPaletteActionId: ${id}`);
};

const DEFAULT_LENGTH = 10;
const getActionMidPoints = ({ id, pos }: { id: BB_ToolPaletteActionId; pos: Point }): MidPoint[] => {
  const { x, y } = pos;

  switch (id) {
    case "CUT_STRAIGHT":
    case "PASS_STRAIGHT":
    case "SCREEN_STRAIGHT":
    case "DRIBBLE_STRAIGHT":
    case "PASS_HASHPASS_STRAIGHT_1":
    case "PASS_HASHPASS_STRAIGHT_2":
    case "PASS_HASHPASS_STRAIGHT_3":
    case "PASS_HASHPASS_STRAIGHT_4":
    case "PASS_HASHPASS_STRAIGHT_5":
    case "PASS_HANDOFF":
    case "DHO":
      return [];

    case "CUT_CURVE":
    case "PASS_CURVE":
    case "SCREEN_CURVE":
    case "DRIBBLE_CURVE":
    case "PASS_HASHPASS_CURVE_1":
    case "PASS_HASHPASS_CURVE_2":
    case "PASS_HASHPASS_CURVE_3":
    case "PASS_HASHPASS_CURVE_4":
    case "PASS_HASHPASS_CURVE_5":
      return [{ x, y: y - DEFAULT_LENGTH, pointType: PointType.CURVE }];

    case "CUT_ANGLE":
    case "SCREEN_ANGLE":
    case "DRIBBLE_ANGLE":
      return [{ x, y: y - DEFAULT_LENGTH, pointType: PointType.ANGLE }];

    case "CUT_ANGLE_CURVE":
    case "SCREEN_ANGLE_CURVE":
    case "DRIBBLE_ANGLE_CURVE":
      return [
        { x: x + DEFAULT_LENGTH, y, pointType: PointType.ANGLE },
        { x: x + DEFAULT_LENGTH, y: y - DEFAULT_LENGTH, pointType: PointType.CURVE },
      ];

    case "CUT_CURVE_ANGLE":
    case "SCREEN_CURVE_ANGLE":
    case "DRIBBLE_CURVE_ANGLE":
      return [
        { x: x + DEFAULT_LENGTH, y, pointType: PointType.CURVE },
        { x: x + DEFAULT_LENGTH, y: y - DEFAULT_LENGTH, pointType: PointType.ANGLE },
      ];

    case "CUT_S_CURVE":
    case "SCREEN_S_CURVE":
    case "DRIBBLE_S_CURVE":
      return [
        { x: x + DEFAULT_LENGTH, y, pointType: PointType.CURVE },
        { x: x + DEFAULT_LENGTH, y: y - DEFAULT_LENGTH, pointType: PointType.CURVE },
      ];

    case "CUT_ANGLE_S_CURVE":
    case "SCREEN_ANGLE_S_CURVE":
    case "DRIBBLE_ANGLE_S_CURVE":
      return [
        { x: x, y: y - DEFAULT_LENGTH, pointType: PointType.ANGLE },
        { x: x + DEFAULT_LENGTH, y: y - DEFAULT_LENGTH, pointType: PointType.CURVE },
        { x: x + DEFAULT_LENGTH, y: y - DEFAULT_LENGTH * 2, pointType: PointType.CURVE },
      ];
  }
};

const getActionEndPos = ({ id, pos }: { id: BB_ToolPaletteActionId; pos: Point }): Point => {
  const { x, y } = pos;

  switch (id) {
    case "CUT_STRAIGHT":
    case "PASS_STRAIGHT":
    case "SCREEN_STRAIGHT":
    case "DRIBBLE_STRAIGHT":
    case "PASS_HASHPASS_STRAIGHT_1":
    case "PASS_HASHPASS_STRAIGHT_2":
    case "PASS_HASHPASS_STRAIGHT_3":
    case "PASS_HASHPASS_STRAIGHT_4":
    case "PASS_HASHPASS_STRAIGHT_5":
      return { x: x + DEFAULT_LENGTH, y };

    case "PASS_HANDOFF":
    case "DHO":
      return { x, y };

    case "CUT_CURVE":
    case "PASS_CURVE":
    case "SCREEN_CURVE":
    case "DRIBBLE_CURVE":
    case "PASS_HASHPASS_CURVE_1":
    case "PASS_HASHPASS_CURVE_2":
    case "PASS_HASHPASS_CURVE_3":
    case "PASS_HASHPASS_CURVE_4":
    case "PASS_HASHPASS_CURVE_5":
    case "CUT_ANGLE":
    case "SCREEN_ANGLE":
    case "DRIBBLE_ANGLE":
      return { x: x + DEFAULT_LENGTH, y: y - DEFAULT_LENGTH };

    case "CUT_ANGLE_CURVE":
    case "SCREEN_ANGLE_CURVE":
    case "DRIBBLE_ANGLE_CURVE":
    case "CUT_CURVE_ANGLE":
    case "SCREEN_CURVE_ANGLE":
    case "DRIBBLE_CURVE_ANGLE":
    case "CUT_S_CURVE":
    case "SCREEN_S_CURVE":
    case "DRIBBLE_S_CURVE":
      return { x: x + DEFAULT_LENGTH * 2, y: y - DEFAULT_LENGTH };

    case "CUT_ANGLE_S_CURVE":
    case "SCREEN_ANGLE_S_CURVE":
    case "DRIBBLE_ANGLE_S_CURVE":
      return { x: x + DEFAULT_LENGTH * 2, y: y - DEFAULT_LENGTH * 2 };
  }
};

const getPlayerActionLine = (id: BB_ToolPaletteCutId | BB_ToolPaletteScreenId): BasketballPlayerActionLineType => {
  if (isBBCutId(id)) return BasketballActionLineType.CUT;
  else if (isBBScreenId(id)) return BasketballActionLineType.SCREEN;
  else throw new Error(`Invalid BB_ToolPaletteActionId: ${id}`);
};

const getBallActionLineType = (id: BB_ToolPalettePassId): BasketballBallActionLineType => {
  switch (id) {
    case "PASS_STRAIGHT":
    case "PASS_CURVE":
      return BasketballActionLineType.PASS;

    case "PASS_HASHPASS_STRAIGHT_1":
    case "PASS_HASHPASS_STRAIGHT_2":
    case "PASS_HASHPASS_STRAIGHT_3":
    case "PASS_HASHPASS_STRAIGHT_4":
    case "PASS_HASHPASS_STRAIGHT_5":
    case "PASS_HASHPASS_CURVE_1":
    case "PASS_HASHPASS_CURVE_2":
    case "PASS_HASHPASS_CURVE_3":
    case "PASS_HASHPASS_CURVE_4":
    case "PASS_HASHPASS_CURVE_5":
      return BasketballActionLineType.HASHPASS;
  }
};

const getNumberOfHashes = (id: BB_ToolPalettePassId): number => {
  switch (id) {
    case "PASS_HASHPASS_STRAIGHT_1":
    case "PASS_HASHPASS_CURVE_1":
      return 1;

    case "PASS_HASHPASS_STRAIGHT_2":
    case "PASS_HASHPASS_CURVE_2":
      return 2;

    case "PASS_HASHPASS_STRAIGHT_3":
    case "PASS_HASHPASS_CURVE_3":
      return 3;

    case "PASS_HASHPASS_STRAIGHT_4":
    case "PASS_HASHPASS_CURVE_4":
      return 4;

    case "PASS_HASHPASS_STRAIGHT_5":
    case "PASS_HASHPASS_CURVE_5":
      return 5;

    default:
      return 0;
  }
};

export interface CreateBasketballStencilFromToolPaletteProps {
  id: BB_ToolPaletteStencilId;
  pos: Point;
}

export const createBasketballStencilFromToolPalette = ({
  id,
  pos,
}: CreateBasketballStencilFromToolPaletteProps): FrameStencil | null => {
  const stencilType = getStencilType(id);

  switch (stencilType) {
    case StencilType.MARKER: {
      if (!isBBMarkerId(id)) return null;
      const markerType = getMarkerType(id);
      const markerBase: MarkerBase = { id: uuid(), pos, stencilType, stencilName: getStencilName(id) };
      switch (markerType) {
        case MarkerType.PLAYER: {
          if (!isBBPlayerId(id)) return null;
          return {
            ...markerBase,
            markerType,
            playerIdNumber: getPlayerIdNumber(id),
            label: getPlayerLabel(id),
            fill: getPlayerFill(id),
            possession: playerHasPossession(id),
            color: "#000",
            movements: [],
          };
        }

        case MarkerType.BALL:
          return { ...markerBase, markerType, associatedPlayer: "", movements: [] };

        case MarkerType.MISC: {
          if (!isBBMiscId(id)) return null;
          return { ...markerBase, markerType, miscType: getMiscType(id) };
        }

        case MarkerType.TEXT:
          return { ...markerBase, markerType, label: "text", width: 20 };

        case MarkerType.SHADING_RECTANGLE:
          return { ...markerBase, markerType, width: 5, height: 10 };

        case MarkerType.SHADING_OVAL:
          return { ...markerBase, markerType, rx: 2.5, ry: 5 };

        case MarkerType.SHADING_TRAPEZOID: {
          const { x, y } = pos;
          return {
            ...markerBase,
            markerType,
            shadeVertices: [
              { x, y },
              { x: x + 10, y },
              { x: x + 7.5, y: y + 10 },
              { x: x + 2.5, y: y + 10 },
            ],
          };
        }

        default:
          return null;
      }
    }
    case StencilType.ACTION: {
      if (!isBBActionId(id)) return null;
      const actionType = getActionType(id);
      const actionBase: ActionBase = {
        id: uuid(),
        stencilType,
        pos,
        midPoints: getActionMidPoints({ id, pos }),
        endPos: getActionEndPos({ id, pos }),
        startTime: 0,
        endTime: 0,
      };

      switch (actionType) {
        case ActionType.PLAYER: {
          if (!isBBCutId(id) && !isBBScreenId(id)) return null;
          return { ...actionBase, actionType, layerType: getPlayerActionLine(id), nextPlayerAction: null };
        }

        case ActionType.BALL: {
          if (!isBBPassId(id)) return null;
          const layerType = getBallActionLineType(id);
          const ballActionBase: BallActionBase = {
            ...actionBase,
            actionType,
            layerType,
            nextBallAction: null,
            nextPlayer: null,
            prevPlayer: null,
            playerActionStart: null,
            playerActionEnd: null,
          };

          if (layerType === BasketballActionLineType.HASHPASS)
            return { ...ballActionBase, numberOfHashes: getNumberOfHashes(id) };
          else return { ...ballActionBase, layerType };
        }

        case ActionType.HANDOFF:
          return {
            ...actionBase,
            actionType,
            layerType: BasketballActionLineType.HANDOFF,
            startPos: actionBase.pos,
            prevPlayer: null,
            nextPlayer: null,
            prevAction: null,
            nextAction: null,
          };

        case ActionType.POSSESSION:
          return {
            ...actionBase,
            actionType,
            layerType: BasketballActionLineType.DRIBBLE,
            nextPlayerAction: null,
            nextBallAction: null,
          };
      }
    }
  }
};
